import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  leadsData: [],
  editedRows: {}, // Object instead of Map for serialization: { leadId: { field: value } }
  loading: false,
  error: null,
  saving: false,
  refreshing: false,
  lastFetched: null,
};

export const salesLeadsSlice = createSlice({
  name: 'salesLeads',
  initialState,
  reducers: {
    // Set all leads data
    setLeadsData: (state, action) => {
      state.leadsData = action.payload;
      state.lastFetched = Date.now();
      state.error = null;
    },

    // Add or update a single lead
    updateLead: (state, action) => {
      const updatedLead = action.payload;
      const index = state.leadsData.findIndex(p => p.id === updatedLead.id);
      if (index !== -1) {
        state.leadsData[index] = { ...state.leadsData[index], ...updatedLead };
      } else {
        state.leadsData.push(updatedLead);
      }
    },

    // Track cell edit (before saving to API)
    trackCellEdit: (state, action) => {
      const { leadId, field, value } = action.payload;

      if (!state.editedRows[leadId]) {
        state.editedRows[leadId] = {};
      }

      state.editedRows[leadId][field] = value;

      // Update the local data immediately for UI responsiveness
      // Create a new array with updated object to maintain immutability properly
      const index = state.leadsData.findIndex(p => p.id === leadId);
      if (index !== -1) {
        state.leadsData[index] = {
          ...state.leadsData[index],
          [field]: value
        };
      }
    },

    // Clear all edited rows after successful save
    clearEditedRows: (state) => {
      state.editedRows = {};
    },

    // Clear edited rows for a specific lead
    clearEditedRowsForLead: (state, action) => {
      const leadId = action.payload;
      delete state.editedRows[leadId];
    },

    // Discard edits and revert to original data
    discardEdits: (state, action) => {
      const leadId = action.payload;

      if (leadId) {
        // Discard edits for specific lead
        delete state.editedRows[leadId];
      } else {
        // Discard all edits
        state.editedRows = {};
      }

      // Note: You may want to refetch from API to get original values
      // This just clears the edited tracking
    },

    // Set loading state
    setLoading: (state, action) => {
      state.loading = action.payload;
    },

    // Set refreshing state
    setRefreshing: (state, action) => {
      state.refreshing = action.payload;
    },

    // Set saving state
    setSaving: (state, action) => {
      state.saving = action.payload;
    },

    // Set error
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset entire state
    resetSalesLeads: (state) => {
      return initialState;
    },
  },
});

export const {
  setLeadsData,
  updateLead,
  trackCellEdit,
  clearEditedRows,
  clearEditedRowsForLead,
  discardEdits,
  setLoading,
  setRefreshing,
  setSaving,
  setError,
  clearError,
  resetSalesLeads,
} = salesLeadsSlice.actions;

// Selectors
export const selectLeadsData = (state) => state.salesLeads.leadsData;
export const selectEditedRows = (state) => state.salesLeads.editedRows;
export const selectEditedRowsCount = (state) => Object.keys(state.salesLeads.editedRows).length;
export const selectLoading = (state) => state.salesLeads.loading;
export const selectRefreshing = (state) => state.salesLeads.refreshing;
export const selectSaving = (state) => state.salesLeads.saving;
export const selectError = (state) => state.salesLeads.error;
export const selectLastFetched = (state) => state.salesLeads.lastFetched;

export default salesLeadsSlice.reducer;
